/**
 * RequestDetails — Modal open/close methods.
 * Mixed into RequestDetails.prototype.
 */
import { Modal } from '../../utils/Modal.js';

export const ModalsMixin = {

    moveWorkflowModalsToBody() {
        ['log_status_modal', 'edit_status_modal', 'payment_edit_modal', 'workflow_notice_modal'].forEach((id) => {
            const el = document.getElementById(id);
            if (el && el.parentElement !== document.body) {
                document.body.appendChild(el);
            }
        });
    },

    showModalElement(modal) {
        if (!modal) return;
        modal.style.display = 'block';
        modal.style.opacity = '1';
    },

    setModalLoading(modalEl, isLoading) {
        const loader = modalEl?.querySelector('.modal-loader');
        if (!loader) return;
        if (isLoading) {
            loader.removeAttribute('hidden');
            loader.setAttribute('aria-hidden', 'false');
        } else {
            loader.setAttribute('hidden', '');
            loader.setAttribute('aria-hidden', 'true');
        }
    },

    setBtnLoading(btnEl, isLoading) {
        if (!btnEl) return;
        if (isLoading) {
            btnEl.dataset.origText = btnEl.innerHTML;
            btnEl.classList.add('loading');
            btnEl.setAttribute('aria-disabled', 'true');
            btnEl.style.pointerEvents = 'none';
            if (btnEl.tagName === 'BUTTON') btnEl.disabled = true;
        } else {
            btnEl.classList.remove('loading');
            btnEl.removeAttribute('aria-disabled');
            btnEl.style.pointerEvents = '';
            if (btnEl.tagName === 'BUTTON') btnEl.disabled = false;
            if (btnEl.dataset.origText) {
                btnEl.innerHTML = btnEl.dataset.origText;
                delete btnEl.dataset.origText;
            }
        }
    },

    hideCancelBtn() {
        const el = document.getElementById('csancel_request_btn')
            || document.getElementById('csancel_pending_info');
        if (el) el.remove();
    },

    // ── Reason for Case Manager ──────────────────────────────────────────────

    openReasonForCaseManagerModal() {
        const modal = document.getElementById('reason_for_case_manager_modal');
        if (!modal) return;
        const select = document.getElementById('reason_for_case_manager_select');
        if (select) select.classList.remove('input-error');
        const noteEl = document.getElementById('reason_for_case_manager_note');
        if (noteEl) noteEl.value = '';
        Modal.open('reason_for_case_manager_modal');
    },

    closeReasonForCaseManagerModal() {
        Modal.close('reason_for_case_manager_modal');
    },

    // ── Cancel Request ───────────────────────────────────────────────────────

    openCancelRequestModal() {
        const modal     = document.getElementById('cancel_request_modal');
        const isCm        = modal?.dataset.cancelRole === 'case_manager';
        const source      = this.details?.querySelector('#cancel_request_cm_blocked_source p');
        const formEl      = document.getElementById('cancel_request_modal_form');
        const blockedEl   = document.getElementById('cancel_request_modal_blocked');
        const msgEl       = document.getElementById('cancel_request_modal_blocked_message');

        if (isCm && source && formEl && blockedEl) {
            if (msgEl) msgEl.textContent = source.textContent.trim();
            formEl.hidden    = true;
            blockedEl.hidden = false;
        } else if (isCm && formEl && blockedEl) {
            formEl.hidden    = false;
            blockedEl.hidden = true;
            const reasonEl = document.getElementById('cancel_request_reason');
            const noteEl   = document.getElementById('cancel_request_note');
            if (reasonEl) { reasonEl.value = ''; reasonEl.classList.remove('input-error'); }
            if (noteEl)   { noteEl.value   = ''; noteEl.classList.remove('input-error'); }
        } else {
            const reasonEl = document.getElementById('cancel_request_reason');
            const noteEl   = document.getElementById('cancel_request_note');
            if (reasonEl) { reasonEl.value = ''; reasonEl.classList.remove('input-error'); }
            if (noteEl)   { noteEl.value   = ''; noteEl.classList.remove('input-error'); }
        }

        Modal.open('cancel_request_modal');
    },

    closeCancelRequestModal() {
        Modal.close('cancel_request_modal');
    },

    // ── Resolve Cancel ───────────────────────────────────────────────────────

    openResolveCancelModal(resolution, cancelIndex) {
        this._resolveCancelResolution = resolution;
        this._resolveCancelIndex = cancelIndex;
        const modal      = document.getElementById('resolve_cancel_modal');
        if (!modal) return;
        const title      = document.getElementById('resolve_cancel_modal_title');
        const desc       = document.getElementById('resolve_cancel_modal_desc');
        const confirmBtn = document.getElementById('resolve_cancel_confirm');
        const noteEl     = document.getElementById('resolve_cancel_note');
        const errorEl    = document.getElementById('resolve_cancel_modal_error');
        if (noteEl)  { noteEl.value = ''; noteEl.classList.remove('input-error'); }
        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
        if (confirmBtn) this.setBtnLoading(confirmBtn, false);
        if (resolution === 'accept') {
            if (title)      title.textContent      = 'Accept & Cancel Request';
            if (desc)       desc.textContent       = 'Please provide a note before canceling the request.';
            if (confirmBtn) { confirmBtn.className = 'button red'; confirmBtn.textContent = 'Accept & Cancel'; }
        } else {
            if (title)      title.textContent      = 'Decline Cancellation Request';
            if (desc)       desc.textContent       = 'Please provide a note explaining why the cancellation request is declined.';
            if (confirmBtn) { confirmBtn.className = 'button white'; confirmBtn.textContent = 'Decline'; }
        }
        Modal.open('resolve_cancel_modal');
    },

    closeResolveCancelModal() {
        Modal.close('resolve_cancel_modal');
        this._resolveCancelResolution = null;
        this._resolveCancelIndex = null;
    },

    // ── Generate Documents ───────────────────────────────────────────────────

    openGenerateRequestDocumentsModal() {
        const modal = document.getElementById('generate_request_documents_modal');
        if (!modal) return;
        this.setModalLoading(modal, false);
        this.setBtnLoading(modal.querySelector('#generate_request_documents_confirm'), false);
        Modal.open('generate_request_documents_modal');
        this.initDocsSortable(modal);
    },

    initDocsSortable(modal) {
        const list = modal?.querySelector('#generate_docs_sortable');
        if (!list) return;
        let dragged = null;
        list.querySelectorAll('.generate-docs-list__item').forEach(item => {
            item.addEventListener('dragstart', () => {
                dragged = item;
                setTimeout(() => item.classList.add('dragging'), 0);
            });
            item.addEventListener('dragend', () => {
                item.classList.remove('dragging');
                list.querySelectorAll('.generate-docs-list__item').forEach(i => i.classList.remove('drag-over'));
                dragged = null;
            });
            item.addEventListener('dragover', e => {
                e.preventDefault();
                if (dragged && dragged !== item) {
                    list.querySelectorAll('.generate-docs-list__item').forEach(i => i.classList.remove('drag-over'));
                    item.classList.add('drag-over');
                }
            });
            item.addEventListener('drop', e => {
                e.preventDefault();
                if (dragged && dragged !== item) {
                    const items = [...list.querySelectorAll('.generate-docs-list__item')];
                    const fromIdx = items.indexOf(dragged);
                    const toIdx   = items.indexOf(item);
                    list.insertBefore(dragged, fromIdx < toIdx ? item.nextSibling : item);
                }
                item.classList.remove('drag-over');
            });
        });
    },

    closeGenerateRequestDocumentsModal() {
        Modal.close('generate_request_documents_modal');
    },

    // ── Escalate ─────────────────────────────────────────────────────────────

    openEscalateModal() {
        const modal = document.getElementById('escalate_request_modal');
        if (!modal) return;
        const note = document.getElementById('escalate_note');
        if (note) note.value = '';
        this.setModalLoading(modal, false);
        this.setBtnLoading(modal.querySelector('#escalate_request_confirm'), false);
        Modal.open('escalate_request_modal');
    },

    closeEscalateModal() {
        Modal.close('escalate_request_modal');
    },

    // ── Report a Problem ─────────────────────────────────────────────────────

    openReportProblemModal() {
        const modal = document.getElementById('report_problem_modal');
        if (!modal) return;
        const messageEl = document.getElementById('report_problem_message');
        const errorEl   = document.getElementById('report_problem_error');
        if (messageEl) { messageEl.value = ''; messageEl.classList.remove('input-error'); }
        if (errorEl)   { errorEl.style.display = 'none'; errorEl.textContent = ''; }
        this.setModalLoading(modal, false);
        this.setBtnLoading(modal.querySelector('#report_problem_confirm'), false);
        Modal.open('report_problem_modal');
    },

    closeReportProblemModal() {
        Modal.close('report_problem_modal');
    },

    // ── File Visibility ──────────────────────────────────────────────────────

    openFileVisibilityModal(contextEl) {
        const modal  = document.getElementById('request_file_visibility_modal');
        if (!modal) return;
        const rid    = contextEl.getAttribute('data-request-id') || '';
        const aid    = contextEl.getAttribute('data-attachment-id') || '';
        const ridIn  = document.getElementById('request_file_visibility_request_id');
        const aidIn  = document.getElementById('request_file_visibility_attachment_id');
        if (ridIn) ridIn.value = rid;
        if (aidIn) aidIn.value = aid;
        const roi   = document.getElementById('request_file_vis_roi');
        const cm    = document.getElementById('request_file_vis_cm');
        const owner = document.getElementById('request_file_vis_owner');
        if (roi)   roi.checked   = contextEl.getAttribute('data-visibility-roi-specialist')   === '1';
        if (cm)    cm.checked    = contextEl.getAttribute('data-visibility-case-manager-portal') === '1';
        if (owner) owner.checked = contextEl.getAttribute('data-visibility-request-owner')    === '1';
        Modal.open('request_file_visibility_modal');
    },

    closeFileVisibilityModal() {
        Modal.close('request_file_visibility_modal');
    },

    // ── Delete File ──────────────────────────────────────────────────────────

    openDeleteFileModal(rid, aid) {
        const modal = document.getElementById('delete_file_modal');
        if (!modal) return;
        modal.dataset.requestId    = rid;
        modal.dataset.attachmentId = aid;
        this.setBtnLoading(modal.querySelector('#delete_file_confirm'), false);
        Modal.open('delete_file_modal');
    },

    closeDeleteFileModal() {
        Modal.close('delete_file_modal');
    },

    // ── Edit File ────────────────────────────────────────────────────────────

    openEditFileModal(rid, aid, category = '', status = '') {
        const modal = document.getElementById('edit_file_modal');
        if (!modal) return;
        modal.dataset.requestId    = rid;
        modal.dataset.attachmentId = aid;
        document.getElementById('edit_file_request_id').value    = rid;
        document.getElementById('edit_file_attachment_id').value = aid;

        const setNiceSelect = (selId, value) => {
            const sel = document.getElementById(selId);
            if (!sel) return;
            sel.value = value;
            const niceEl = sel.parentElement?.querySelector('.nice-select');
            if (!niceEl) return;
            const matched    = niceEl.querySelector(`.option[data-value="${CSS.escape(value)}"]`);
            const currentEl  = niceEl.querySelector('.current');
            if (matched && currentEl) {
                currentEl.textContent = matched.textContent;
                niceEl.querySelectorAll('.option').forEach(o => {
                    o.classList.toggle('selected', o.dataset.value === value);
                    o.classList.toggle('focus',    o.dataset.value === value);
                });
            }
        };

        setNiceSelect('edit_file_category', category);
        setNiceSelect('edit_file_status', status);
        Modal.open('edit_file_modal');
    },

    closeEditFileModal() {
        Modal.close('edit_file_modal');
    },

    // ── Edit Patient Details ─────────────────────────────────────────────────

    openEditPatientDetailsModal() {
        const modal = document.getElementById('edit_patient_details_modal');
        if (!modal) return;
        const wrap = this.details?.querySelector('.request_details_wrapper');
        const rid  = wrap?.dataset?.requestId;
        if (!rid || rid === '-1') return;
        const ridInput  = document.getElementById('edit_patient_request_id');
        const nameInput = document.getElementById('edit_patient_name');
        const dobInput  = document.getElementById('edit_patient_date_of_birth');
        const ssnInput  = document.getElementById('edit_patient_ssn');
        const addrInput = document.getElementById('edit_patient_address');
        const treatFromInput = document.getElementById('edit_treatment_from');
        const treatToInput   = document.getElementById('edit_treatment_to');
        if (ridInput)        ridInput.value       = rid;
        if (nameInput)       nameInput.value      = wrap.dataset.patientName || '';
        if (dobInput)        dobInput.value       = this._datePicker?.toFullFormat?.(wrap.dataset.patientDob || '') ?? (wrap.dataset.patientDob || '');
        if (ssnInput)        ssnInput.value       = wrap.dataset.patientSsn || '';
        if (addrInput)       addrInput.value      = wrap.dataset.patientAddress || '';
        if (treatFromInput)  treatFromInput.value = wrap.dataset.treatmentFrom || '';
        if (treatToInput)    treatToInput.value   = wrap.dataset.treatmentTo   || '';
        Modal.open('edit_patient_details_modal');
    },

    closeEditPatientDetailsModal() {
        Modal.close('edit_patient_details_modal');
    },

    setNiceSelectValue(selectId, value) {
        const sel = document.getElementById(selectId);
        if (!sel) return;
        sel.value = value;
        const niceEl = sel.parentElement?.querySelector('.nice-select');
        if (!niceEl) return;
        const matched = niceEl.querySelector(`.option[data-value="${CSS.escape(String(value))}"]`);
        const currentEl = niceEl.querySelector('.current');
        if (matched && currentEl) {
            currentEl.textContent = matched.textContent;
            niceEl.querySelectorAll('.option').forEach((o) => {
                o.classList.toggle('selected', o.dataset.value === String(value));
                o.classList.toggle('focus', o.dataset.value === String(value));
            });
        }
    },

    // The real <select> behind nice-select2 is `display:none` (see
    // .hidden-select in style.scss), so it's barred from native constraint
    // validation and never fires an "invalid" event on its own — required
    // selects using nice-select2 have to be validated and highlighted manually.
    setNiceSelectInvalid(selectEl, isInvalid) {
        const niceEl = selectEl?.parentElement?.querySelector('.nice-select');
        niceEl?.classList.toggle('invalid', !!isInvalid);
    },

    openEditCaseManagerModal() {
        const modal = document.getElementById('edit_request_case_manager_modal');
        if (!modal) return;
        const wrap = this.details?.querySelector('.request_details_wrapper');
        const rid = wrap?.dataset?.requestId;
        if (!rid || rid === '-1') return;
        const ridInput = document.getElementById('edit_case_manager_request_id');
        if (ridInput) ridInput.value = rid;
        this.setNiceSelectValue('edit_case_manager_select', wrap.dataset.caseManagerId || '');
        Modal.open('edit_request_case_manager_modal');
    },

    closeEditCaseManagerModal() {
        Modal.close('edit_request_case_manager_modal');
    },

    openEditRoiSpecialistModal() {
        const modal = document.getElementById('edit_request_roi_specialist_modal');
        if (!modal) return;
        const wrap = this.details?.querySelector('.request_details_wrapper');
        const rid = wrap?.dataset?.requestId;
        if (!rid || rid === '-1') return;
        const ridInput = document.getElementById('edit_roi_specialist_request_id');
        if (ridInput) ridInput.value = rid;
        this.setNiceSelectValue('edit_roi_specialist_select', wrap.dataset.roiSpecialistId || '');
        Modal.open('edit_request_roi_specialist_modal');
    },

    closeEditRoiSpecialistModal() {
        Modal.close('edit_request_roi_specialist_modal');
    },

    // ── Reset Invoice ────────────────────────────────────────────────────────

    openResetInvoiceModal(btn) {
        this._resetInvoiceBtn = btn;
        Modal.open('reset_invoice_modal');
    },

    closeResetInvoiceModal() {
        Modal.close('reset_invoice_modal');
        this._resetInvoiceBtn = null;
    },

    // ── Delete Invoice ───────────────────────────────────────────────────────

    openDeleteInvoiceModal(btn) {
        this._deleteInvoiceBtn = btn;
        Modal.open('delete_invoice_modal');
    },

    closeDeleteInvoiceModal() {
        Modal.close('delete_invoice_modal');
        this._deleteInvoiceBtn = null;
    },

    // ── Log Status ───────────────────────────────────────────────────────────

    openLogStatusModal(form, btn) {
        this._logStatusForm = form;
        this._logStatusBtn  = btn;
        this.moveWorkflowModalsToBody();
        const modal  = document.getElementById('log_status_modal');
        if (!modal) return;
        const noteEl  = document.getElementById('log_status_note');
        const errorEl = document.getElementById('log_status_modal_error');
        if (noteEl)  { noteEl.value = ''; noteEl.classList.remove('input-error'); }
        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
        this.showModalElement(modal);
        noteEl?.focus();
    },

    closeLogStatusModal() {
        const modal = document.getElementById('log_status_modal');
        if (!modal) return;
        modal.style.opacity = 0;
        setTimeout(() => { modal.style.display = 'none'; }, 200);
        this._logStatusForm = null;
        this._logStatusBtn  = null;
    },

    // ── Workflow Notice (generic error/warning popup) ───────────────────────

    openWorkflowNoticeModal(message, title = 'Notice') {
        this.moveWorkflowModalsToBody();
        const modal    = document.getElementById('workflow_notice_modal');
        if (!modal) { window.alert(message); return; }
        const titleEl   = document.getElementById('workflow_notice_modal_title');
        const messageEl = document.getElementById('workflow_notice_modal_message');
        if (titleEl)   titleEl.textContent   = title;
        if (messageEl) messageEl.textContent = message;
        this.showModalElement(modal);
    },

    closeWorkflowNoticeModal() {
        Modal.close('workflow_notice_modal');
    },
};
